import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';

import { evictAccountingAllocation, evictPayrollAllocation } from '@neo.tax/shared/apollo';
import {
  ExcelParseError,
  ProviderType,
  UPLOAD_RD_EXPENSE_PROJECT_ALLOCATION_REPORT,
  UploadRdExpenseProjectAllocationReportMutation,
  UploadRdExpenseProjectAllocationReportMutationVariables,
} from '@neo.tax/shared/graphql';
import { UploadExpenseProjectAllocationReportStatus } from '@neo.tax/shared/organisms/bulk-edit-expense-project-allocation-report-modal/hooks/upload-expense-project-allocation-report-status.enums';

interface UseUploadRdExpenseProjectAllocationReport {
  status: UploadExpenseProjectAllocationReportStatus;
  errors?: ExcelParseError[] | null;
  upload: (file: File) => Promise<void>;
  reset: () => void;
}

export const useUploadRdExpenseProjectAllocationReport = (
  fiscalYearId: string,
  providerType: ProviderType,
): UseUploadRdExpenseProjectAllocationReport => {
  const [status, setStatus] = useState<UploadExpenseProjectAllocationReportStatus>(
    UploadExpenseProjectAllocationReportStatus.NOT_STARTED,
  );
  const [uploadRdExpenseProjectAllocationReport, { data, error }] = useMutation<
    UploadRdExpenseProjectAllocationReportMutation,
    UploadRdExpenseProjectAllocationReportMutationVariables
  >(UPLOAD_RD_EXPENSE_PROJECT_ALLOCATION_REPORT);

  const result = data?.uploadRdExpenseProjectAllocationReport;

  useEffect(() => {
    if (result?.errors || error) {
      setStatus(UploadExpenseProjectAllocationReportStatus.ERROR);
    }
  }, [result, error]);

  const reset = (): void => {
    setStatus(UploadExpenseProjectAllocationReportStatus.NOT_STARTED);
  };

  const upload = async (file: File): Promise<void> => {
    setStatus(UploadExpenseProjectAllocationReportStatus.UPLOADING);
    const { data: mutationData } = await uploadRdExpenseProjectAllocationReport({
      variables: {
        fiscalYearId,
        uploadRdExpenseProjectAllocationReportInput: { providerType },
        file,
      },
      update: () => {
        if (providerType === ProviderType.Payroll) {
          evictPayrollAllocation();
        } else if (providerType === ProviderType.Accounting) {
          evictAccountingAllocation();
        }
      },
    });
    const hasErrors = (mutationData?.uploadRdExpenseProjectAllocationReport?.errors?.length ?? 0) > 0;
    setStatus(
      hasErrors ? UploadExpenseProjectAllocationReportStatus.ERROR : UploadExpenseProjectAllocationReportStatus.SUCCESS,
    );
  };

  return {
    status,
    errors: result?.errors,
    upload,
    reset,
  };
};
